/**
 * NetworkSection Component
 * 流量與網路配置區塊
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */

import type { TrafficConfig } from '../../types/card';
import type { Translations } from '../../i18n';

interface NetworkSectionProps {
  traffic: TrafficConfig;
  onTrafficChange: (traffic: TrafficConfig) => void;
  t: Translations;
}

const trafficUnits = ['GB', 'TB'] as const;
const speedUnits = ['Mbps', 'Gbps'] as const;

export function NetworkSection({ traffic, onTrafficChange, t }: NetworkSectionProps) {
  const handleChange = <K extends keyof TrafficConfig>(
    field: K,
    value: TrafficConfig[K]
  ) => {
    onTrafficChange({ ...traffic, [field]: value });
  };

  const trafficDirections = [
    { value: '雙向計算', label: t.directionBoth },
    { value: '只算入站', label: t.directionIn },
    { value: '只算出站', label: t.directionOut },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">{t.networkConfig}</h3>

      {/* 無限流量開關 */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="unlimited-traffic"
          checked={traffic.isUnlimited}
          onChange={(e) => handleChange('isUnlimited', e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="unlimited-traffic" className="text-sm font-medium text-gray-700">
          {t.unlimitedTraffic}
        </label>
      </div>

      {/* 流量配置（非無限流量時顯示） */}
      {!traffic.isUnlimited && (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.traffic}</label>
            <input
              type="number"
              value={traffic.value}
              onChange={(e) => handleChange('value', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              min="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.unit}</label>
            <select
              value={traffic.unit}
              onChange={(e) => handleChange('unit', e.target.value as 'GB' | 'TB')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {trafficUnits.map((unit) => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t.direction}</label>
            <select
              value={traffic.direction}
              onChange={(e) => handleChange('direction', e.target.value as TrafficConfig['direction'])}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {trafficDirections.map((dir) => (
                <option key={dir.value} value={dir.value}>{dir.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* 端口速率 */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.portSpeed}</label>
          <input
            type="number"
            value={traffic.speed}
            onChange={(e) => handleChange('speed', parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            step="0.1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.speedUnit}</label>
          <select
            value={traffic.speedUnit}
            onChange={(e) => handleChange('speedUnit', e.target.value as 'Mbps' | 'Gbps')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {speedUnits.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 限速說明 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t.throttleInfo}
        </label>
        <input
          type="text"
          value={traffic.throttleInfo}
          onChange={(e) => handleChange('throttleInfo', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="例: 超量後限速至 10Mbps"
        />
      </div>
    </div>
  );
}
